import React, { useEffect } from 'react';
import Loader from 'modules/kiosk/components/Loader';
import { useDispatch, useSelector } from 'react-redux';
import { commonTripSelector } from 'modules/common/CommonTrip/selectors';
import { Badge, Carousel, Image, Button } from 'react-bootstrap';
import { Document, DocumentPicture } from 'entities/Document/interfaces';
import { useImageOnLoad } from 'modules/shared/hooks';
import history from 'browserHistory';
import { cleanTrip } from 'modules/common/CommonTrip/actions';
import { cleanTripFields } from 'modules/common/FillTrip';
import { PhoneService } from '../../../../entities/Phone/service';
import styles from './CreatedTripInfo.module.scss';

type PictureItem = {
  documentNumber: string | number;
  picture: string;
};

const CreatedTripInfo: React.FC = () => {
  const dispatch = useDispatch();
  const { loadingTrip, errorTrip, trip } = useSelector(commonTripSelector);

  const { handleImageOnLoad, css } = useImageOnLoad();

  const getPictures = (): PictureItem[] => {
    const pictures: PictureItem[] = [];

    trip?.driver?.documents?.forEach((document: Document) => {
      document?.pictures?.forEach((item: DocumentPicture) =>
        pictures.push({
          documentNumber: item.documentNumber || '',
          picture: item.picture,
        }),
      );
    });

    trip?.vehicle?.document?.pictures?.forEach((item: DocumentPicture) =>
      pictures.push({
        documentNumber: trip.vehicle.document.number,
        picture: item.picture,
      }),
    );

    if (trip?.trailer?.document) {
      trip.trailer.document?.pictures?.forEach((item: DocumentPicture) =>
        pictures.push({
          documentNumber: trip.trailer.document.number,
          picture: item.picture,
        }),
      );
    }

    return pictures;
  };

  const goToMainScreen = () => {
    dispatch(cleanTrip());
    dispatch(cleanTripFields());
    history.push('/kiosk');
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      goToMainScreen();
    }, 30000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <section className={styles.Container}>
      {loadingTrip && <Loader />}
      {errorTrip?.message ? (
        <p>{errorTrip.message}</p>
      ) : (
        <>
          <h1 className={styles.PageTitle}>Вітаємо, рейс було зареєстровано</h1>

          <h3 className={styles.BlockTitle}>Водій</h3>
          <table className="table">
            <thead>
              <tr>
                <th>Прізвище</th>
                <th>Ім&apos;я</th>
                <th>По-батькові</th>
                <th>Телефон</th>
                <th>Посвідчення водія</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>{trip?.driver?.lastName}</td>
                <td>{trip?.driver?.firstName}</td>
                <td>{trip?.driver?.middleName}</td>
                <td>
                  {typeof trip?.driver?.phone?.number === 'string'
                    ? PhoneService.formatPhone(trip.driver.phone?.number)
                    : ''}
                </td>
                <td>{trip?.driver?.documents?.find((document: Document) => document.type === 1)?.number}</td>
              </tr>
            </tbody>
          </table>

          <h3 className={styles.BlockTitle}>Авто</h3>

          <table className="table">
            <thead>
              <tr>
                <th>Номер авто</th>
                <th>Техпаспорт</th>
                <th>Номер причепа</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>{trip?.vehicle?.licensePlate}</td>
                <td>{trip?.vehicle?.document?.number}</td>
                <td>{trip?.trailer?.licensePlate || ' - '}</td>
              </tr>
            </tbody>
          </table>

          <div className={styles.PicturesContainer}>
            <Carousel interval={null}>
              {getPictures()?.map((item: PictureItem, index: number) => (
                <Carousel.Item key={index} className="fade-in border-0 w-100">
                  <Image
                    className={styles.ImgDocument}
                    src={item?.picture}
                    alt="Фото недоступне"
                    onLoad={handleImageOnLoad}
                    style={{ ...css.fullSize }}
                    rounded
                  />
                  <Badge variant="secondary" className={styles.BadgeDocument}>
                    {item.documentNumber}
                  </Badge>
                </Carousel.Item>
              ))}
            </Carousel>
          </div>

          <Button active variant="outline-danger" onClick={goToMainScreen}>
            ЗАКРИТИ
          </Button>
        </>
      )}
    </section>
  );
};

export default CreatedTripInfo;
